import type { GeoPoint } from '@tla/types';

const EARTH_RADIUS_M = 6_371_000;
const DEG2RAD = Math.PI / 180;

/** Great-circle distance in metres between two [lon, lat] points. */
export function haversine(a: GeoPoint, b: GeoPoint): number {
  const lat1 = a[1] * DEG2RAD;
  const lat2 = b[1] * DEG2RAD;
  const dLat = (b[1] - a[1]) * DEG2RAD;
  const dLon = (b[0] - a[0]) * DEG2RAD;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Project a [lon, lat] point to local planar metres using an equirectangular
 * projection centred on `centre`. Suitable for the small extent of a race track.
 */
export function projectLocal(point: GeoPoint, centre: GeoPoint): [number, number] {
  const x = (point[0] - centre[0]) * DEG2RAD * Math.cos(centre[1] * DEG2RAD) * EARTH_RADIUS_M;
  const y = (point[1] - centre[1]) * DEG2RAD * EARTH_RADIUS_M;
  return [x, y];
}

/**
 * Inverse of {@link projectLocal}: convert local planar metres [x, y] back to a
 * geographic [lon, lat] point, given the same projection centre.
 */
export function unprojectLocal(point: [number, number], centre: GeoPoint): GeoPoint {
  const lon = centre[0] + point[0] / (DEG2RAD * Math.cos(centre[1] * DEG2RAD) * EARTH_RADIUS_M);
  const lat = centre[1] + point[1] / (DEG2RAD * EARTH_RADIUS_M);
  return [lon, lat];
}

/** Orientation sign of the ordered triplet (p, q, r). */
function orient(p: [number, number], q: [number, number], r: [number, number]): number {
  const v = (q[1] - p[1]) * (r[0] - q[0]) - (q[0] - p[0]) * (r[1] - q[1]);
  if (Math.abs(v) < 1e-12) return 0;
  return v > 0 ? 1 : -1;
}

/**
 * True if segment p1-p2 intersects segment p3-p4. Uses local planar coordinates;
 * callers should project geographic points first for consistency, but for the
 * small scales involved raw lon/lat also works adequately.
 */
export function segmentsIntersect(
  p1: [number, number],
  p2: [number, number],
  p3: [number, number],
  p4: [number, number],
): boolean {
  const d1 = orient(p3, p4, p1);
  const d2 = orient(p3, p4, p2);
  const d3 = orient(p1, p2, p3);
  const d4 = orient(p1, p2, p4);
  return d1 !== d2 && d3 !== d4;
}
